import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';

import { PageHeader } from '../../components/layout/PageHeader.js';
import { formatDateTime } from '../../utils/date.js';
import { Badge } from '../../components/ui/Badge.js';
import { Button } from '../../components/ui/Button.js';
import {
  useAuthorizeQbo,
  useCompleteQboAuthorization,
  useDisconnectQbo,
  useQboConnection,
  useQboSettings,
  useRefreshQboConnection,
} from './api.js';
import { SettingsEditor } from './SettingsEditor.js';
import { PaymentSyncCard } from './PaymentSyncCard.js';

const STATE_STORAGE_KEY = 'qbo.oauth.state';
/** Marker for the message the OAuth popup posts back to the opener window. */
const OAUTH_MESSAGE_TYPE = 'qbo-oauth-callback';

/** True when this page is running inside the OAuth popup (opened by Connect). */
function isOAuthPopup(): boolean {
  return Boolean(window.opener && window.opener !== window);
}

const STATUS_TONES = {
  connected: 'green',
  expired: 'amber',
  disconnected: 'gray',
} as const;

const STATUS_LABELS = {
  connected: 'Connected',
  expired: 'Token expired',
  disconnected: 'Not connected',
} as const;

interface DetailItemProps {
  label: string;
  value: string;
}

function DetailItem({ label, value }: DetailItemProps): JSX.Element {
  return (
    <div>
      <dt className="text-xs uppercase tracking-wide text-slate-500">{label}</dt>
      <dd className="mt-1 text-sm font-medium text-slate-900">{value}</dd>
    </div>
  );
}

export function QboIntegrationPage(): JSX.Element {
  const [searchParams, setSearchParams] = useSearchParams();
  const connectionQuery = useQboConnection();
  const settingsQuery = useQboSettings();
  const authorize = useAuthorizeQbo();
  const complete = useCompleteQboAuthorization();
  const refresh = useRefreshQboConnection();
  const disconnect = useDisconnectQbo();

  const [callbackError, setCallbackError] = useState<string | null>(null);
  const [editingSettings, setEditingSettings] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const handledCallback = useRef(false);
  const popupRef = useRef<Window | null>(null);

  // Verifies the CSRF state, then exchanges the code for tokens. Shared by both the
  // popup (message) path and the full-page redirect fallback.
  const completeCallback = useCallback(
    (code: string, realmId: string, state?: string): void => {
      const storedState = sessionStorage.getItem(STATE_STORAGE_KEY) ?? undefined;
      sessionStorage.removeItem(STATE_STORAGE_KEY);
      if (state && storedState && state !== storedState) {
        setCallbackError('The QuickBooks response failed a security check (state mismatch).');
        return;
      }
      complete.mutate(
        { code, realmId, state },
        {
          onError: (err) =>
            setCallbackError((err as Error)?.message ?? 'Could not complete the connection.'),
        },
      );
    },
    [complete],
  );

  // When Intuit redirects back to this page with ?code&realmId, finish the flow.
  // Inside the popup we just relay the params to the opener and close; otherwise
  // (the full-page redirect fallback) we complete the exchange here.
  useEffect(() => {
    if (handledCallback.current) return;
    const code = searchParams.get('code');
    const realmId = searchParams.get('realmId');
    if (!code || !realmId) return;

    handledCallback.current = true;
    const state = searchParams.get('state') ?? undefined;

    if (isOAuthPopup()) {
      window.opener?.postMessage(
        { type: OAUTH_MESSAGE_TYPE, code, realmId, state },
        window.location.origin,
      );
      window.close();
      return;
    }

    completeCallback(code, realmId, state);
    setSearchParams({}, { replace: true });
  }, [searchParams, setSearchParams, completeCallback]);

  // In the opener window, listen for the popup relaying the OAuth result back.
  useEffect(() => {
    function onMessage(event: MessageEvent): void {
      if (event.origin !== window.location.origin) return;
      const data = event.data as
        | { type?: string; code?: string; realmId?: string; state?: string }
        | null;
      if (!data || data.type !== OAUTH_MESSAGE_TYPE || !data.code || !data.realmId) return;
      if (popupRef.current && !popupRef.current.closed) popupRef.current.close();
      popupRef.current = null;
      setConnecting(false);
      completeCallback(data.code, data.realmId, data.state);
    }
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [completeCallback]);

  // Reset the connecting state if the user closes the popup without authorizing.
  useEffect(() => {
    if (!connecting) return;
    const timer = window.setInterval(() => {
      if (!popupRef.current || popupRef.current.closed) {
        popupRef.current = null;
        setConnecting(false);
      }
    }, 500);
    return () => window.clearInterval(timer);
  }, [connecting]);

  function handleConnect(): void {
    setCallbackError(null);
    authorize.mutate(undefined, {
      onSuccess: (result) => {
        sessionStorage.setItem(STATE_STORAGE_KEY, result.state);
        // Open Intuit in a popup; fall back to a full-page redirect if it's blocked.
        const popup = window.open(
          result.authorizationUrl,
          'qbo-oauth',
          'width=620,height=760,menubar=no,toolbar=no,location=no,status=no',
        );
        if (popup) {
          popupRef.current = popup;
          popup.focus();
          setConnecting(true);
        } else {
          window.location.href = result.authorizationUrl;
        }
      },
    });
  }

  const connection = connectionQuery.data;
  const settings = settingsQuery.data;
  const isConnected = connection?.status === 'connected' || connection?.status === 'expired';
  const busy = authorize.isPending || complete.isPending || connecting;

  return (
    <div>
      <PageHeader
        title="QuickBooks Online"
        description="Connect Catalyst to your QuickBooks Online company to push invoices."
      />

      <div className="mb-6 rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-sm font-semibold text-slate-700">Setup</h2>
          {settings && !editingSettings ? (
            <Button variant="secondary" onClick={() => setEditingSettings(true)}>
              Edit settings
            </Button>
          ) : null}
        </div>

        {settingsQuery.isLoading ? (
          <p className="text-sm text-slate-500">Loading settings…</p>
        ) : settingsQuery.isError ? (
          <p className="text-sm text-rose-600">Could not load the QuickBooks settings.</p>
        ) : settings ? (
          <>
            <dl className="grid grid-cols-2 gap-4 sm:grid-cols-3">
              <DetailItem label="Client ID" value={settings.clientId ?? '—'} />
              <DetailItem
                label="Client secret"
                value={settings.hasClientSecret ? 'Stored' : 'Not set'}
              />
              <DetailItem label="Redirect URI" value={settings.redirectUri ?? '—'} />
              <DetailItem label="Default item" value={settings.defaultItemName ?? settings.defaultItemId ?? '—'} />
              <DetailItem label="Minor version" value={settings.minorVersion ?? '—'} />
              <DetailItem
                label="Push mode"
                value={settings.dryRun ? 'Dry run (simulated)' : 'Live (posts to QBO)'}
              />
            </dl>

            {editingSettings ? (
              <SettingsEditor settings={settings} onClose={() => setEditingSettings(false)} />
            ) : null}
          </>
        ) : null}
      </div>

      <div className="mb-6 rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-sm font-semibold text-slate-700">Connection</h2>
          {connection ? (
            <Badge tone={STATUS_TONES[connection.status]}>
              {STATUS_LABELS[connection.status]}
            </Badge>
          ) : null}
        </div>

        {connectionQuery.isLoading ? (
          <p className="text-sm text-slate-500">Loading connection…</p>
        ) : connectionQuery.isError ? (
          <p className="text-sm text-rose-600">Could not load the QuickBooks connection.</p>
        ) : connection ? (
          <>
            {!connection.configured ? (
              <p className="mb-4 rounded-lg bg-amber-50 px-3 py-2 text-sm text-amber-800">
                QuickBooks client credentials are not configured yet. Use{' '}
                <button
                  type="button"
                  onClick={() => setEditingSettings(true)}
                  className="font-medium underline"
                >
                  Edit settings
                </button>{' '}
                above to enter the Client ID, Client secret and Redirect URI, then
                connect.
              </p>
            ) : null}

            <dl className="grid grid-cols-2 gap-4 sm:grid-cols-3">
              <DetailItem label="Company (realm)" value={connection.realmId ?? '—'} />
              <DetailItem label="Environment" value={connection.environment} />
              <DetailItem
                label="Push mode"
                value={connection.dryRun ? 'Dry run (simulated)' : 'Live (posts to QBO)'}
              />
              <DetailItem label="Connected" value={formatDateTime(connection.connectedAt)} />
              <DetailItem
                label="Last refreshed"
                value={formatDateTime(connection.lastRefreshedAt)}
              />
              <DetailItem
                label="Token expires"
                value={formatDateTime(connection.tokenExpiresAt)}
              />
            </dl>

            <div className="mt-5 flex flex-wrap items-center gap-2">
              {isConnected ? (
                <>
                  <Button
                    variant="secondary"
                    onClick={() => refresh.mutate()}
                    isLoading={refresh.isPending}
                  >
                    Refresh token
                  </Button>
                  <Button
                    variant="danger"
                    onClick={() => disconnect.mutate()}
                    isLoading={disconnect.isPending}
                  >
                    Disconnect
                  </Button>
                </>
              ) : (
                <Button
                  onClick={handleConnect}
                  disabled={!connection.configured || busy}
                  isLoading={busy}
                >
                  {connecting ? 'Waiting for QuickBooks…' : 'Connect to QuickBooks'}
                </Button>
              )}
            </div>

            {callbackError ? (
              <p className="mt-3 text-sm text-rose-600">{callbackError}</p>
            ) : null}
            {refresh.isError ? (
              <p className="mt-3 text-sm text-rose-600">
                {(refresh.error as Error)?.message ?? 'Could not refresh the token.'}
              </p>
            ) : null}
          </>
        ) : null}
      </div>

      <PaymentSyncCard />

      <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
        <h2 className="mb-2 text-sm font-semibold text-slate-700">Send invoices</h2>
        <p className="text-sm text-slate-600">
          Push draft and in-review invoices to QuickBooks from the{' '}
          <Link to="/invoices/qbo-batch" className="font-medium text-brand-600 hover:underline">
            Send to QuickBooks
          </Link>{' '}
          page.
        </p>
      </div>
    </div>
  );
}
